import { addHours } from "date-fns"
import { useEffect } from "react"
import { parseFirstPromoterCookies } from "src/integrations/firstPromoter/cookies"
import { createPersistentStore } from "src/utilities/localStorage/create"

type State = {
  initializing: boolean
  suppressed: boolean
  updatedAt: string
}

const defaultState: State = {
  initializing: true,
  suppressed: false,
  updatedAt: "",
}

const store = createPersistentStore(() => defaultState, { name: "adcell" })

const suppressionTimeoutHours = 48

export function useMountAdcell() {
  const initializing = store((state) => state.initializing)
  const suppressed = store((state) => state.suppressed)

  return !initializing && !suppressed
}

function canStateChange() {
  const lastUpdated = store.getState().updatedAt

  if (!lastUpdated) {
    return true
  }

  const lastUpdatedPlusTimeout = addHours(lastUpdated, suppressionTimeoutHours).toISOString()
  const now = new Date().toISOString()

  return now > lastUpdatedPlusTimeout
}

export function useManageIsAdcellSuppressed() {
  useEffect(() => {
    // Example search params:
    // ?bid=290891-0-MERCHANT_TRACKING_TEST&adcref=www.adcell.de%2F

    const search = new URLSearchParams(window.location.search)
    const bid = search.get("bid")
    const adcref = search.get("adcref")
    const fpr = search.get("fpr") || parseFirstPromoterCookies()._fprom_ref

    if (adcref && bid?.includes("MERCHANT_TRACKING_TEST")) {
      store.setState({ initializing: false, suppressed: false, updatedAt: new Date().toISOString() })
      console.debug(
        "🅰️ Adcell is not suppressed because adcref and bid are present and and bid includes MERCHANT_TRACKING_TEST",
        search,
      )
    } else if (fpr) {
      store.setState({ initializing: false, suppressed: true, updatedAt: new Date().toISOString() })
      console.debug("🅰️ Adcell is suppressed because the user is alreaedy attributed to First Promoter", { fpr })
    } else if (canStateChange()) {
      const suppressed = !adcref
      store.setState({ initializing: false, suppressed, updatedAt: new Date().toISOString() })
      console.debug(
        `🅰️ Adcell is ${suppressed ? "suppressed" : "not suppressed"} because ${adcref ? "acref is present" : "the user is alreaedy attributed to a different source"}`,
      )
    } else {
      const suppressed = store.getState().suppressed
      console.debug(
        `🅰️ Adcell remains ${suppressed ? "suppressed" : "not suppressed"} because it was updated less than ${suppressionTimeoutHours} hours ago`,
      )
      store.setState({ initializing: false })
    }
  }, [])
}
